/** Firma e timbro del cliente (`/api/customers/{id}/firma`): PNG senza sfondo salvati nella cartella Drive del cliente. */
export type SignatureKind = 'firma' | 'timbro'

export interface SignatureImage {
  documentId: number
  url: string
}

export interface CustomerSignatureView {
  firma: SignatureImage | null
  timbro: SignatureImage | null
  canGenerateStamp: boolean
}

async function csrfHeaders(): Promise<Record<string, string>> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (!res.ok) return {}
  const data = (await res.json()) as { token: string }
  return { 'X-XSRF-TOKEN': data.token }
}

async function read<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { title?: string; message?: string; error?: string } | null
    throw new Error(body?.error ?? body?.message ?? body?.title ?? `HTTP ${res.status}`)
  }
  return (await res.json()) as T
}

const base = (customerId: number) => `/api/customers/${customerId}/firma`

export const customerSignatureApi = {
  get: async (customerId: number): Promise<CustomerSignatureView> =>
    read(await fetch(base(customerId), { credentials: 'include' })),

  /** Firma o timbro disegnati sullo schermo (data URL PNG). */
  saveDrawing: async (customerId: number, kind: SignatureKind, dataUrl: string): Promise<SignatureImage> =>
    read(await fetch(`${base(customerId)}/disegno`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...(await csrfHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, dataUrl }),
    })),

  /** Foto o scansione su foglio bianco: il server toglie lo sfondo. */
  uploadPhoto: async (customerId: number, kind: SignatureKind, file: File): Promise<SignatureImage> => {
    const form = new FormData()
    form.append('Kind', kind)
    form.append('File', file)
    return read(await fetch(`${base(customerId)}/foto`, {
      method: 'POST', credentials: 'include', headers: await csrfHeaders(), body: form,
    }))
  },

  /** Timbro generato da ragione sociale, indirizzo e P.IVA del cliente. */
  generateStamp: async (customerId: number): Promise<SignatureImage> =>
    read(await fetch(`${base(customerId)}/timbro/genera`, {
      method: 'POST', credentials: 'include', headers: await csrfHeaders(),
    })),
}
